import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import Loader from 'components/loader/Loader';
import { NAV_ITEMS } from 'constants/navigation';
import { ROUTES } from 'constants/routePaths';
import { useAuth } from 'hooks/useAuth';
import { AuthLayout } from 'layouts/AuthLayout';
import { DashboardLayout } from 'layouts/DashboardLayout';
import { ProtectedRoute } from 'routes/ProtectedRoute';
import NoModules from 'views/common/NoModules';

// Route guards come in two kinds, deliberately:
//
//   resource="x"            — a MODULE route. Permission alone decides, so a
//                             role that was granted the module reaches it and a
//                             role that had it withheld does not, whatever its
//                             name. These used to also carry
//                             allowedRoles={['superadmin','center_admin']} with
//                             match="any", i.e. role OR permission — which let a
//                             center admin open a module its matrix no longer
//                             granted, and blocked anyone else the matrix DID
//                             grant.
//
//   allowedRoles={[...]}    — a whole-PANEL route (master, super admin, founder,
//                             jury). Pages stay role-wise; these guard which
//                             panel you are in, not which module you may open.
import { PublicRoute } from 'routes/PublicRoute';
import { isJuryMemberRole, isMentorRole } from 'util/access';

// Lazy-loaded pages — code-split per route.
const Login = lazy(() => import('views/auth/Login/Login'));
const Register = lazy(() => import('views/auth/Register/Register'));
const ForgotPassword = lazy(() => import('views/auth/ForgotPassword/ForgotPassword'));
const SetPassword = lazy(() => import('views/auth/SetPassword/SetPassword'));
const SetInitialPassword = lazy(() => import('views/auth/SetInitialPassword/SetInitialPassword'));
const DashboardHome = lazy(() => import('views/dashboard/DashboardHome'));
const OnboardingForm = lazy(() => import('views/master/OnboardingForm'));
const SuperAdmins = lazy(() => import('views/master/SuperAdmins'));
const SuperAdminForm = lazy(() => import('views/master/SuperAdminForm'));
const SuperAdminView = lazy(() => import('views/master/SuperAdminView'));
const Profile = lazy(() => import('views/profile/Profile'));
const StakeholderProfile = lazy(() => import('views/profile/StakeholderProfile'));
const Settings = lazy(() => import('views/profile/Settings'));
const Notifications = lazy(() => import('views/notifications/Notifications'));
const Messages = lazy(() => import('views/chat/Messages'));
const TodosHome = lazy(() => import('views/todos/TodosHome'));
const TodoListView = lazy(() => import('views/todos/TodoListView'));
const TodoDetail = lazy(() => import('views/todos/TodoDetail'));
const MyInvitations = lazy(() => import('views/todos/MyInvitations'));
// Collaboration module — the named-collaboration container flow.
const CollaborationsList = lazy(() => import('views/collaboration/CollaborationsList'));
const CollaborationDetail = lazy(() => import('views/collaboration/CollaborationDetail'));
const CollaborationInfo = lazy(() => import('views/collaboration/CollaborationInfo'));
const CollaborationForm = lazy(() => import('views/collaboration/CollaborationForm'));
const ManageCollaboratorsScreen = lazy(() => import('views/collaboration/ManageCollaboratorsScreen'));
const ProgramCollaborationManage = lazy(() => import('views/collaboration/ProgramCollaborationManage'));
const CenterProfile = lazy(() => import('views/center/CenterProfile'));
// UNDER CONSTRUCTION — withdrawn 2026-09-11, along with its route below and
// the User menu entry that linked to it (components/layout/UserMenu.jsx).
// const CenterNotificationPreferences = lazy(() => import('views/center/CenterNotificationPreferences'));
const OrganizationProfile = lazy(() => import('views/organization/OrganizationProfile'));
const MentoringList = lazy(() => import('views/mentoring/MentoringList'));
const MentoringSessionView = lazy(() => import('views/mentoring/MentoringSessionView'));
const DocumentVerification = lazy(() => import('views/documents/DocumentVerification'));
const DocumentVerificationStartup = lazy(() => import('views/documents/DocumentVerificationStartup'));
const OpenPrograms = lazy(() => import('views/founder/OpenPrograms'));
const FounderEvents = lazy(() => import('views/founder/FounderEvents'));
const FounderProgramView = lazy(() => import('views/founder/FounderProgramView'));
const ApplyForm = lazy(() => import('views/founder/ApplyForm'));
const PublicApplyForm = lazy(() => import('views/founder/PublicApplyForm'));
const MyMentors = lazy(() => import('views/founder/MyMentors'));
const MyMentorDetail = lazy(() => import('views/founder/MyMentorDetail'));
const MySessions = lazy(() => import('views/founder/MySessions'));
const MySessionDetail = lazy(() => import('views/founder/MySessionDetail'));
const MyFunding = lazy(() => import('views/founder/MyFunding'));
const FundingDetail = lazy(() => import('views/founder/FundingDetail'));
const Reports = lazy(() => import('views/reports/Reports'));
const ReportRouter = lazy(() => import('views/reports/ReportRouter'));
const JuryPrograms = lazy(() => import('views/jury/JuryPrograms'));
const JuryProgramApplications = lazy(() => import('views/jury/JuryProgramApplications'));
const JuryApplicationView = lazy(() => import('views/jury/JuryApplicationView'));
const ScheduledSessions = lazy(() => import('views/mentor/ScheduledSessions'));
const SessionForm = lazy(() => import('views/mentor/SessionForm'));
const AssignedStartups = lazy(() => import('views/mentor/AssignedStartups'));
const AssignedStartupView = lazy(() => import('views/mentor/AssignedStartupView'));
const SessionScheduling = lazy(() => import('views/mentor/SessionScheduling'));
const StartupProfile = lazy(() => import('views/founder/StartupProfile'));
const StartupProfileEdit = lazy(() => import('views/startups/StartupProfileEdit'));
const RolesUsers = lazy(() => import('views/roles/RolesUsers'));
const UsersList = lazy(() => import('views/roles/UsersList'));
const RolesList = lazy(() => import('views/roles/RolesList'));
const RoleEditor = lazy(() => import('views/roles/RoleEditor/RoleEditor'));
const RoleView = lazy(() => import('views/roles/RoleView'));
const CentersList = lazy(() => import('views/centers/CentersList'));
const CenterView = lazy(() => import('views/centers/CenterView'));
const CenterForm = lazy(() => import('views/centers/CenterForm'));
const StartupsList = lazy(() => import('views/startups/StartupsList'));
const StartupForm = lazy(() => import('views/startups/StartupForm'));
const StartupView = lazy(() => import('views/startups/StartupView'));
const UserView = lazy(() => import('views/users/UserView'));
const UserForm = lazy(() => import('views/users/UserForm'));
const UserPermissions = lazy(() => import('views/users/UserPermissions'));
const ProgramsList = lazy(() => import('views/programs/ProgramsList'));
const CreateProgram = lazy(() => import('views/programs/wizard/CreateProgram'));
const MyReviews = lazy(() => import('views/programs/MyReviews'));
const EvaluationProcessList = lazy(() => import('views/programs/evaluation/EvaluationProcessList'));
const EvaluationProcessBuilder = lazy(() => import('views/programs/evaluation/EvaluationProcessBuilder'));
const ProgramView = lazy(() => import('views/programs/ProgramView'));
const ProgramApplications = lazy(() => import('views/programs/ApplicationsList'));
const ApplicationDetails = lazy(() => import('views/programs/ApplicationDetails'));
const ApplicationCompleted = lazy(() => import('views/programs/ApplicationCompleted'));
const EvaluationTemplatesList = lazy(() => import('views/evaluations/EvaluationTemplatesList'));
const EvaluationTemplateBuilder = lazy(() => import('views/evaluations/EvaluationTemplateBuilder'));
const EvaluationTemplateView = lazy(() => import('views/evaluations/EvaluationTemplateView'));
const FundsList = lazy(() => import('views/funds/FundsList'));
const FundWizard = lazy(() => import('views/funds/wizard/FundWizard'));
const FundView = lazy(() => import('views/funds/FundView'));
const StakeholdersList = lazy(() => import('views/stakeholders/StakeholdersList'));
const StakeholderView = lazy(() => import('views/stakeholders/StakeholderView'));
const MentorAssignments = lazy(() => import('views/stakeholders/MentorAssignments'));
const AssignmentDetails = lazy(() =>
  import('views/stakeholders/MentorAssignments').then((m) => ({ default: m.AssignmentDetails })),
);
// UNDER CONSTRUCTION — see the note in constants/navigation.js.
// const CenterCalendar = lazy(() => import('views/calendar/CenterCalendar'));
const EventsList = lazy(() => import('views/events/EventsList'));
const EventView = lazy(() => import('views/events/EventView'));
const EventForm = lazy(() => import('views/events/EventForm'));
const SpeakersList = lazy(() => import('views/speakers/SpeakersList'));
const SpeakerForm = lazy(() => import('views/speakers/SpeakerForm'));
const SpeakerView = lazy(() => import('views/speakers/SpeakerView'));
const ComingSoon = lazy(() => import('views/common/ComingSoon'));

// Sidebar tabs that don't yet have a dedicated screen get a placeholder.
// EVERY nav path with a real <Route> below must be listed here — a path that is
// missing gets a SECOND route registered for it further down as <ComingSoon />.
// Today the real route still wins (equal path score, and it is declared first),
// but the duplicate is one reorder away from silently replacing a working screen.
const BUILT_PATHS = [
  ROUTES.DASHBOARD,
  ROUTES.TODOS,
  ROUTES.TODO_INVITATIONS,
  ROUTES.ONBOARDING_NEW,
  ROUTES.SUPER_ADMINS,
  ROUTES.REPORTS,
  '/dashboard/roles',
  ROUTES.CENTERS,
  ROUTES.CENTER_REGISTER,
  ROUTES.STARTUPS,
  ROUTES.PROGRAMS,
  ROUTES.PROGRAM_NEW,
  ROUTES.EVALUATION_PROCESSES,
  ROUTES.EVALUATION_PROCESS_NEW,
  ROUTES.EVALUATION_TEMPLATES,
  ROUTES.EVALUATION_TEMPLATE_NEW,
  ROUTES.FUNDS,
  ROUTES.FUND_NEW,
  ROUTES.STAKEHOLDERS,
  ROUTES.STAKEHOLDER_ASSIGN,
  // ROUTES.CALENDAR,  // withdrawn with the Calendar screen
  ROUTES.EVENTS,
  ROUTES.EVENT_NEW,
  ROUTES.SPEAKERS,
  ROUTES.SPEAKER_NEW,

  // Role-panel screens (founder / mentor / jury / reviewer). These were all
  // built but never added here, so each had a duplicate ComingSoon route.
  ROUTES.OPEN_PROGRAMS,
  ROUTES.MY_MENTORS,
  ROUTES.MY_SESSIONS,
  ROUTES.MY_FUNDING,
  ROUTES.STARTUP_PROFILE,
  '/dashboard/my-events', // no ROUTES constant; literal in navigation.js too
  ROUTES.MY_REVIEWS,
  ROUTES.JURY_PROGRAMS,
  ROUTES.MENTOR_CALENDAR,
  ROUTES.MENTOR_SESSIONS,
  ROUTES.ASSIGNED_STARTUPS,
  ROUTES.MENTORING,
  ROUTES.DOCUMENT_VERIFICATION,
];
const PLACEHOLDER_ITEMS = NAV_ITEMS.filter((item) => !BUILT_PATHS.includes(item.path));

// Jury members see a dedicated "Applicant Details" + scoring screen; everyone
// else gets the full admin/founder application view. A user who HOLDS the jury
// role (including a mentor+jury user) gets the jury scoring screen so they can
// submit their own juror score; only a pure mentor/admin/founder gets the full view.
function ApplicationDetailsByRole() {
  const { role, user } = useAuth();
  const held = new Set((user?.assignments || []).map((a) => String(a).toLowerCase()));
  const hasJury = isJuryMemberRole(role) || held.has('jury');
  return hasJury ? <JuryApplicationView /> : <ApplicationDetails />;
}

// Mentor/jury panel users see the full 7-section stakeholder profile
// (personal/professional/domain/engagement/role/compliance/banking);
// everyone else keeps the generic account-settings-style profile.
function ProfileByRole() {
  const { role } = useAuth();
  return isMentorRole(role) || isJuryMemberRole(role) ? <StakeholderProfile /> : <Profile />;
}

export function AppRoutes() {
  return (
    <Suspense fallback={<Loader fullscreen />}>
      <Routes>
        {/* Public (unauthenticated) routes */}
        <Route element={<PublicRoute />}>
          <Route element={<AuthLayout />}>
            <Route path={ROUTES.LOGIN} element={<Login />} />
            <Route path={ROUTES.REGISTER} element={<Register />} />
            <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />
            <Route path={ROUTES.SET_PASSWORD} element={<SetPassword />} />
          </Route>
        </Route>

        {/* Public apply-by-link — no auth required, and reachable even when
            already signed in (e.g. an admin previewing their own share link),
            so it sits outside both PublicRoute and ProtectedRoute. */}
        <Route path="/apply/:token" element={<PublicApplyForm />} />

        {/* Friendly vanity apply link — https://.../<center_slug>/<program_slug>.
            A two-segment DYNAMIC route: React Router ranks it BELOW every static
            route (/apply/:token, /login, /dashboard/*, …), so it only catches
            genuine two-segment center/program paths and shows the page's own
            "not found" when the slug pair doesn't resolve. */}
        <Route path="/:centerSlug/:programSlug" element={<PublicApplyForm />} />

        {/* Bare vanity apply link for a platform-wide (no-center) program —
            https://.../<program_slug>. Same ranking reasoning as the two-segment
            route above: a single dynamic segment still ranks below every static
            route (/login, /apply/:token, /dashboard, …), so it only catches a
            genuinely unmatched single segment and shows the page's own
            "not found" when the slug doesn't resolve to a program. */}
        <Route path="/:programSlug" element={<PublicApplyForm />} />

        {/* First-login password reset — authenticated but outside the dashboard shell.
            ProtectedRoute force-redirects every other authenticated route here whenever
            the signed-in user's `mustChangePassword` flag is set. */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AuthLayout />}>
            <Route path={ROUTES.FORCE_PASSWORD_CHANGE} element={<SetInitialPassword />} />
          </Route>
        </Route>

        {/* Protected routes inside the app shell */}
        <Route element={<ProtectedRoute />}>
          <Route element={<DashboardLayout />}>
            <Route path={ROUTES.DASHBOARD} element={<DashboardHome />} />
            {/* Landing for a role that holds no module at all. Ungated on
                purpose — it is where the guards send people, so gating it
                would loop. */}
            <Route path={ROUTES.NO_MODULES} element={<NoModules />} />

            {/* Platform master modules (create/manage super admins) —
                static path BEFORE the dynamic :superAdminId detail route */}
            <Route element={<ProtectedRoute allowedRoles={['master']} />}>
              <Route path={ROUTES.ONBOARDING_NEW} element={<OnboardingForm />} />
              <Route path={ROUTES.SUPER_ADMIN_NEW} element={<SuperAdminForm />} />
              <Route path={ROUTES.SUPER_ADMINS} element={<SuperAdmins />} />
              <Route path="/dashboard/super-admins/:superAdminId" element={<SuperAdminView />} />
            </Route>

            {/* Account (header user menu) */}
            <Route path={ROUTES.PROFILE} element={<ProfileByRole />} />
            <Route path={ROUTES.SETTINGS} element={<Settings />} />
            {/* Tabbed form of the same screen (/dashboard/settings/activities).
                An unknown tab — including the retired `calendar` one, now on the
                Calendar screen — falls back to Security rather than 404ing. */}
            <Route path={`${ROUTES.SETTINGS}/:tab`} element={<Settings />} />
            {/* The header's bell and message icons already check these; their
                full-page counterparts did not, so the module was still
                reachable by URL. Both routers gate on the matching :read. */}
            <Route element={<ProtectedRoute resource="notifications" action="read" />}>
              <Route path={ROUTES.NOTIFICATIONS} element={<Notifications />} />
            </Route>
            <Route element={<ProtectedRoute resource="chat" action="read" />}>
              <Route path={ROUTES.MESSAGES} element={<Messages />} />
            </Route>
            <Route path={ROUTES.CENTER_PROFILE} element={<CenterProfile />} />
            {/* Notification Preferences — UNDER CONSTRUCTION, withdrawn
                2026-09-11. Restore with its lazy import above and the User
                menu entry in components/layout/UserMenu.jsx. */}
            {/*
            <Route
              path={ROUTES.CENTER_NOTIFICATION_PREFERENCES}
              element={<CenterNotificationPreferences />}
            />
            */}
            {/* Organization Profile — a super admin maintains the name and
                logo that head their own panel. The `super_admin` capability
                already includes master (see ProtectedRoute's PRINCIPAL_FAMILY);
                master has no organization of its own, so the screen shows its
                empty state for them. */}
            <Route element={<ProtectedRoute allowedRoles={['super_admin']} />}>
              <Route path={ROUTES.ORGANIZATION_PROFILE} element={<OrganizationProfile />} />
            </Route>

            {/* To-dos (shared workspace for every role) — list home, a single
                list, then a single to-do's detail screen. */}
            {/* My Invitations stays OUTSIDE the guard on purpose: it is where
                someone answers an invitation sent to them, and an invitee who
                holds no Workspace grant must still be able to clear it. Same
                reasoning as todo_collab.py leaving accept/decline ungated. */}
            <Route path={ROUTES.TODO_INVITATIONS} element={<MyInvitations />} />
            <Route element={<ProtectedRoute resource="todos" action="read" />}>
              <Route path={ROUTES.TODOS} element={<TodosHome />} />
              <Route path="/dashboard/todos/lists/:listId" element={<TodoListView />} />
              <Route path="/dashboard/todos/:todoId" element={<TodoDetail />} />
            </Route>

            {/* Collaboration — the invitee side (My Invitations / Shared with me)
                is available to every signed-in user, deliberately ungated: an
                invitee holding no `collaboration` grant still has to be able to
                see and clear an invitation sent to them, or sharing would be
                defeated (see app/routers/collaboration.py). The deep-link
                routes below REUSE the existing module views under this
                auth-only guard so a collaborator can open the shared record
                without holding module-wide permission. The backend enforces the
                actual per-record grant (read vs write) on every API call, so
                these routes never widen access on their own. Existing module
                routes are untouched. */}
            {/* Collaboration module — a list of named collaborations, each a
                container owning organizations, programs and events. */}
            <Route path={ROUTES.COLLABORATIONS} element={<CollaborationsList />} />
            <Route path={ROUTES.COLLABORATION_CREATE} element={<CollaborationForm />} />
            {/* Manage a record's collaborators — the new collaboration-title-search
                flow: pick/create a collaboration, attach this record to it, and
                manage its organizations + members. Replaces the old per-record
                "Select organizations" page (still reached from Programs/Events). */}
            <Route path="/dashboard/collaborations/manage/:resource/:recordId" element={<ProgramCollaborationManage />} />
            {/* Program/Event detail reached from inside a collaboration (the
                `?group=` query param drives the collaboration context banner).
                Same page the regular Programs module opens (ApplicationsList,
                with its Applications / Events & Sessions / Mentoring Sessions
                / Program Details tabs) — kept on this permissive route (no
                `applications:read` ProtectedRoute wrapper) so a collaborator
                without that module-wide permission can still reach it on
                their per-record grant alone, same as before. */}
            <Route path="/dashboard/collaborations/programs/:programId" element={<ProgramApplications />} />
            <Route path="/dashboard/collaborations/events/:eventId" element={<EventView />} />
            {/* One collaboration: detail (Programs/Events tabs), info, edit. */}
            <Route path="/dashboard/collaborations/:id" element={<CollaborationDetail />} />
            <Route path="/dashboard/collaborations/:id/info" element={<CollaborationInfo />} />
            <Route path="/dashboard/collaborations/:id/edit" element={<CollaborationForm />} />
            {/* [H] Manage Collaborators — per-program/event assignment. */}
            <Route
              path="/dashboard/collaborations/:id/records/:resource/:recordId/collaborators"
              element={<ManageCollaboratorsScreen />}
            />

            {/* My Reviews (shared) — applications awaiting the signed-in user's
                step review; the backend scopes the list to their assignments. */}
            <Route path={ROUTES.MY_REVIEWS} element={<MyReviews />} />

            {/* Reports (super admin, center admin, founder — role-aware content).
                reports.py gates its routes on `reports:read`, so without the
                grant the page rendered its shell and every card showed "—".
                Reports is read-only by nature (READ_ONLY_RESOURCES), so there
                is no write half to guard. */}
            <Route element={<ProtectedRoute resource="reports" action="read" />}>
              <Route path={ROUTES.REPORTS} element={<Reports />} />
              <Route path="/dashboard/reports/:reportId" element={<ReportRouter />} />
            </Route>

            {/* Center-admin modules */}
            <Route element={<ProtectedRoute resource="mentoring_sessions" action="read" />}>
              <Route path={ROUTES.MENTORING} element={<MentoringList />} />
              <Route path="/dashboard/mentoring/:sessionId" element={<MentoringSessionView />} />
            </Route>
            {/* The centre's review queue. A founder's `documents:*` is their own
                startup's documents (Startup Profile → Documents), never this. */}
            <Route
              element={<ProtectedRoute resource="documents" action="read" excludeRoles={['startup_founder']} />}
            >
              <Route path={ROUTES.DOCUMENT_VERIFICATION} element={<DocumentVerification />} />
              <Route
                path="/dashboard/document-verification/:startupId"
                element={<DocumentVerificationStartup />}
              />
            </Route>

            {/* Startup-founder modules.

                Each group carries BOTH the role guard and its module's verb, so
                hiding the sidebar item is no longer the only thing stopping
                access — typing the URL is refused too. Grouped by resource
                because these five pages are five different modules; they only
                shared a guard because the guard was role-only.

                Startup Profile is deliberately NOT resource-guarded: it is the
                founder's own record and must stay reachable however the
                Startups verb is set (§7). Its EDIT route requires write, which
                is where that permission actually bites. */}
            <Route element={<ProtectedRoute allowedRoles={['startup_founder']} resource="applications" action="read" />}>
              <Route path={ROUTES.OPEN_PROGRAMS} element={<OpenPrograms />} />
              <Route path="/dashboard/open-programs/:programId" element={<FounderProgramView />} />
            </Route>
            <Route element={<ProtectedRoute allowedRoles={['startup_founder']} resource="applications" action="write" />}>
              <Route path="/dashboard/open-programs/:programId/apply" element={<ApplyForm />} />
            </Route>
            <Route element={<ProtectedRoute allowedRoles={['startup_founder']} resource="mentors" action="read" />}>
              <Route path={ROUTES.MY_MENTORS} element={<MyMentors />} />
              <Route path="/dashboard/my-mentors/:mentorId" element={<MyMentorDetail />} />
            </Route>
            <Route element={<ProtectedRoute allowedRoles={['startup_founder']} resource="events" action="read" />}>
              <Route path="/dashboard/my-events" element={<FounderEvents />} />
            </Route>
            <Route element={<ProtectedRoute allowedRoles={['startup_founder']} resource="mentoring_sessions" action="read" />}>
              <Route path={ROUTES.MY_SESSIONS} element={<MySessions />} />
              <Route path="/dashboard/my-sessions/:sessionId" element={<MySessionDetail />} />
            </Route>
            <Route element={<ProtectedRoute allowedRoles={['startup_founder']} resource="funding" action="read" />}>
              <Route path={ROUTES.MY_FUNDING} element={<MyFunding />} />
              <Route path="/dashboard/my-funding/:fundingId" element={<FundingDetail />} />
            </Route>
            <Route element={<ProtectedRoute allowedRoles={['startup_founder']} />}>
              <Route path={ROUTES.STARTUP_PROFILE} element={<StartupProfile />} />
            </Route>
            <Route element={<ProtectedRoute allowedRoles={['startup_founder']} resource="startups" action="write" />}>
              <Route path={ROUTES.STARTUP_PROFILE_EDIT} element={<StartupProfileEdit />} />
            </Route>

            {/* Jury-member modules */}
            <Route element={<ProtectedRoute allowedRoles={['jury_member']} resource="applications" action="read" />}>
              <Route path={ROUTES.JURY_PROGRAMS} element={<JuryPrograms />} />
              <Route
                path="/dashboard/jury-programs/:programId/applications"
                element={<JuryProgramApplications />}
              />
            </Route>

            {/* Mentor modules.

                These had NO guard at all — any signed-in user could open them by
                URL. Gated on the module verb rather than the role name, so a
                centre admin holding the same verb is unaffected (the pages scope
                their data to the mentor either way).

                The Calendar is on READ, and the sidebar lists it on the same
                verb — no Sessions permission, no Calendar. Creating and editing
                a session are separate routes and DO require write. */}
            <Route element={<ProtectedRoute resource="mentoring_sessions" action="read" />}>
              <Route path={ROUTES.MENTOR_CALENDAR} element={<SessionScheduling />} />
              <Route path={ROUTES.MENTOR_SESSIONS} element={<ScheduledSessions />} />
            </Route>
            <Route element={<ProtectedRoute resource="mentoring_sessions" action="write" />}>
              <Route path={ROUTES.MENTOR_SESSION_NEW} element={<SessionScheduling autoOpenSchedule />} />
              <Route path="/dashboard/mentor-sessions/:sessionId/edit" element={<SessionForm mode="edit" />} />
            </Route>
            <Route element={<ProtectedRoute resource="startups" action="read" />}>
              <Route path={ROUTES.ASSIGNED_STARTUPS} element={<AssignedStartups />} />
              <Route path="/dashboard/assigned-startups/:startupId" element={<AssignedStartupView />} />
            </Route>

            {/* Roles & Users — one Role Editor row, `users`, governs both halves
                (see NAV_RESOURCE: team -> users, roles -> users), matching the
                backend, where roles.py and admin.py gate on users:read /
                users:write.

                These routes carried NO guard at all: the sidebar item hid when
                the row was unticked, but every screen behind it — including the
                Add User form, the Role Editor and the per-user permission
                editor — stayed reachable by URL. READ opens the lists and the
                detail views; every editor needs WRITE.

                Router v7 ranks by specificity, not declaration order, so the
                static /users/* paths still win over the dynamic :roleId even
                though they now sit in a different group. */}
            <Route element={<ProtectedRoute resource="users" action="read" />}>
              <Route path="/dashboard/roles" element={<RolesUsers />}>
                <Route index element={<UsersList />} />
                <Route path="list" element={<RolesList />} />
              </Route>
              <Route path="/dashboard/roles/users/:userId" element={<UserView />} />
              <Route path="/dashboard/roles/:roleId" element={<RoleView />} />
            </Route>
            <Route element={<ProtectedRoute resource="users" action="write" />}>
              <Route path="/dashboard/roles/new" element={<RoleEditor />} />
              <Route path="/dashboard/roles/users/new" element={<UserForm />} />
              <Route path="/dashboard/roles/users/:userId/edit" element={<UserForm />} />
              <Route path="/dashboard/roles/users/:userId/permissions" element={<UserPermissions />} />
              <Route path="/dashboard/roles/:roleId/edit" element={<RoleEditor />} />
            </Route>

            {/* Centers & Startups (super admin) — static paths BEFORE dynamic :id */}
            {/* Centers — one row (`centers`, which also carries platform:centers)
                governs the module. These carried no guard, so a super admin
                whose Centers row was unticked could still open a centre's full
                record by URL: the API's only check there was
                `assert_center_access`, which asks which centres you can REACH,
                not whether the module is open to you. Both now apply. */}
            <Route element={<ProtectedRoute resource="centers" action="read" />}>
              <Route path={ROUTES.CENTERS} element={<CentersList />} />
              <Route path="/dashboard/centers/:centerId" element={<CenterView />} />
            </Route>
            <Route element={<ProtectedRoute resource="centers" action="write" />}>
              <Route path={ROUTES.CENTER_REGISTER} element={<CenterForm mode="create" />} />
              <Route path="/dashboard/centers/:centerId/edit" element={<CenterForm mode="edit" />} />
            </Route>
            {/* Startups — these carried no guard: the sidebar item hid when
                the row was unticked, but every screen stayed reachable by URL,
                and a cross-module link (a speaker's Upcoming Events) walked
                straight into one. READ opens the list and the detail view,
                WRITE opens the editors — the same split Programs, Evaluations
                and Users & Roles already use. A record-scoped collaborator
                still passes: ProtectedRoute ORs in hasResourceGrant. */}
            <Route element={<ProtectedRoute resource="startups" action="read" />}>
              <Route path={ROUTES.STARTUPS} element={<StartupsList />} />
              <Route path="/dashboard/startups/:startupId" element={<StartupView />} />
            </Route>
            <Route element={<ProtectedRoute resource="startups" action="write" />}>
              <Route path={ROUTES.STARTUP_NEW} element={<StartupForm />} />
              {/* Edit uses the tabbed profile editor (loads existing data per section). */}
              <Route path="/dashboard/startups/:startupId/edit" element={<StartupProfileEdit />} />
            </Route>

            {/* Programs (super admin + center admin, OR a Team Member holding
                `applications:read`/`write` via the Role Editor) — split into a
                read group (list/view/applications) and a write group
                (new/edit), so a read-only grant can browse without also being
                able to author. Static paths BEFORE dynamic :id */}
            <Route
              element={(
                <ProtectedRoute
                  resource="applications"
                  action="read"
                />
              )}
            >
              <Route path={ROUTES.PROGRAMS} element={<ProgramsList />} />
              <Route path="/dashboard/programs/:programId" element={<ProgramView />} />
              <Route path="/dashboard/programs/:programId/applications" element={<ProgramApplications />} />
            </Route>
            {/* Program create: normally applications:write, but an accepted
                collaboration member may also create a DRAFT program from inside a
                collaboration via ?collab=<groupId> (membership re-authorised by
                the backend on save). Edit keeps the strict write gate. */}
            <Route
              element={(
                <ProtectedRoute
                  resource="applications"
                  action="write"
                  bypassParam="collab"
                />
              )}
            >
              <Route path={ROUTES.PROGRAM_NEW} element={<CreateProgram />} />
            </Route>
            <Route
              element={(
                <ProtectedRoute
                  resource="applications"
                  action="write"
                />
              )}
            >
              <Route path="/dashboard/programs/:programId/edit" element={<CreateProgram mode="edit" />} />
            </Route>

            {/* Evaluation Process templates — any admin, OR any role holding
                `evaluations:read`/`write` (which Programs write implies), so a
                custom center role granted Programs write can author them
                without also being an admin, and one granted just read can
                browse. Static paths before dynamic :id. */}
            <Route
              element={(
                <ProtectedRoute
                  resource="evaluations"
                  action="read"
                />
              )}
            >
              <Route path={ROUTES.EVALUATION_PROCESSES} element={<EvaluationProcessList />} />
            </Route>
            <Route
              element={(
                <ProtectedRoute
                  resource="evaluations"
                  action="write"
                />
              )}
            >
              <Route path={ROUTES.EVALUATION_PROCESS_NEW} element={<EvaluationProcessBuilder />} />
              <Route path="/dashboard/evaluation-processes/:id/edit" element={<EvaluationProcessBuilder />} />
            </Route>
            {/* Applicant detail is shared: admin reviews it, the founder views their
                own submission, a jury member gets the scoring variant, and a step
                REVIEWER (any assigned center staff) can open it too. Role is not
                restricted here — the backend `assert_application_access` enforces who
                may actually open a given application; the view is read-only unless the
                caller can act on the current step. */}
            <Route
              path="/dashboard/programs/:programId/applications/:applicationId"
              element={<ApplicationDetailsByRole />}
            />
            {/* Legacy /completed URL — the completed state now renders in
                place on the row above (ApplicationDetailsByRole) instead of a
                separate page, so this just bounces anyone who lands here
                (an old bookmark/notification link) to that view. */}
            <Route
              path="/dashboard/programs/:programId/applications/:applicationId/completed"
              element={<ApplicationCompleted />}
            />

            {/* Evaluation matrix — same gate as Evaluation Processes above: any
                admin, or any role holding `evaluations:read`/`write`. List
                and view are read-accessible; new/edit need write. Static
                paths BEFORE dynamic :id */}
            <Route
              element={(
                <ProtectedRoute
                  resource="evaluations"
                  action="read"
                />
              )}
            >
              <Route path={ROUTES.EVALUATION_TEMPLATES} element={<EvaluationTemplatesList />} />
              <Route
                path="/dashboard/evaluation-matrix/:templateId"
                element={<EvaluationTemplateView />}
              />
            </Route>
            <Route
              element={(
                <ProtectedRoute
                  resource="evaluations"
                  action="write"
                />
              )}
            >
              <Route path={ROUTES.EVALUATION_TEMPLATE_NEW} element={<EvaluationTemplateBuilder />} />
              <Route
                path="/dashboard/evaluation-matrix/:templateId/edit"
                element={<EvaluationTemplateBuilder />}
              />
            </Route>

            {/* Fund catalog (super admin) — static paths BEFORE dynamic :id */}
            {/* Fund catalog — these carried no guard: the sidebar item hid when
                the row was unticked, but every screen stayed reachable by URL,
                and a cross-module link (a speaker's Upcoming Events) walked
                straight into one. READ opens the list and the detail view,
                WRITE opens the editors — the same split Programs, Evaluations
                and Users & Roles already use. A record-scoped collaborator
                still passes: ProtectedRoute ORs in hasResourceGrant. */}
            <Route element={<ProtectedRoute resource="funds" action="read" />}>
              <Route path={ROUTES.FUNDS} element={<FundsList />} />
              <Route path="/dashboard/funds/:fundId" element={<FundView />} />
            </Route>
            <Route element={<ProtectedRoute resource="funds" action="write" />}>
              <Route path={ROUTES.FUND_NEW} element={<FundWizard />} />
              <Route path="/dashboard/funds/:fundId/edit" element={<FundWizard />} />
            </Route>

            {/* Stakeholders (super admin) — static paths BEFORE dynamic :id */}
            {/* Stakeholders — one row (`mentors`) owns the Mentors and Jury tabs
                and the assignment flow, matching the backend. These carried no
                guard, so the whole module was reachable by URL with the row
                unticked.

                All four sit under READ on purpose: the assignments page shows
                the list AND the create form, and read-only has to keep the
                list. The component drops the create flow itself when the write
                grant is missing, so the page stays useful instead of being shut
                behind a write gate the reader legitimately fails. */}
            <Route element={<ProtectedRoute resource="mentors" action="read" />}>
              <Route path={ROUTES.STAKEHOLDER_ASSIGN} element={<MentorAssignments />} />
              <Route path="/dashboard/stakeholders/assignments/:assignmentId" element={<AssignmentDetails />} />
              <Route path={ROUTES.STAKEHOLDERS} element={<StakeholdersList />} />
              <Route path="/dashboard/stakeholders/:stakeholderId" element={<StakeholderView />} />
            </Route>

            {/* Common calendar (super admin) — events AND mentoring sessions
                on one grid, center by center. Gated the same way as the two
                modules it draws: any admin, or a role holding `events:read`
                (the backend scopes each feed independently on every call, so a
                caller with only one of the two simply sees only that one). */}
            {/* Calendar — UNDER CONSTRUCTION, withdrawn 2026-09-11. Restore
                this block together with its nav item (constants/navigation.js),
                its lazy import above and its BUILT_PATHS entry. */}
            {/*
            <Route
              element={(
                <ProtectedRoute
                  resource="events"
                  action="read"
                />
              )}
            >
              <Route path={ROUTES.CALENDAR} element={<CenterCalendar />} />
            </Route>
            */}

            {/* Events (super admin) — static paths BEFORE dynamic :id */}
            {/* Events — these carried no guard: the sidebar item hid when
                the row was unticked, but every screen stayed reachable by URL,
                and a cross-module link (a speaker's Upcoming Events) walked
                straight into one. READ opens the list and the detail view,
                WRITE opens the editors — the same split Programs, Evaluations
                and Users & Roles already use. A record-scoped collaborator
                still passes: ProtectedRoute ORs in hasResourceGrant. */}
            <Route element={<ProtectedRoute resource="events" action="read" />}>
              <Route path={ROUTES.EVENTS} element={<EventsList />} />
              <Route path="/dashboard/events/:eventId" element={<EventView />} />
            </Route>
            <Route element={<ProtectedRoute resource="events" action="write" />}>
              <Route path={ROUTES.EVENT_NEW} element={<EventForm mode="create" />} />
              <Route path="/dashboard/events/:eventId/edit" element={<EventForm mode="edit" />} />
            </Route>

            {/* Speakers (super admin) — static paths BEFORE dynamic :id */}
            {/* Speakers — these carried no guard: the sidebar item hid when
                the row was unticked, but every screen stayed reachable by URL,
                and a cross-module link (a speaker's Upcoming Events) walked
                straight into one. READ opens the list and the detail view,
                WRITE opens the editors — the same split Programs, Evaluations
                and Users & Roles already use. A record-scoped collaborator
                still passes: ProtectedRoute ORs in hasResourceGrant. */}
            <Route element={<ProtectedRoute resource="speakers" action="read" />}>
              <Route path={ROUTES.SPEAKERS} element={<SpeakersList />} />
              <Route path="/dashboard/speakers/:speakerId" element={<SpeakerView />} />
            </Route>
            <Route element={<ProtectedRoute resource="speakers" action="write" />}>
              <Route path={ROUTES.SPEAKER_NEW} element={<SpeakerForm mode="create" />} />
              <Route path="/dashboard/speakers/:speakerId/edit" element={<SpeakerForm mode="edit" />} />
            </Route>

            {/* Placeholders for the remaining tabs */}
            {PLACEHOLDER_ITEMS.map((item) => (
              <Route key={item.key} path={item.path} element={<ComingSoon title={item.label} />} />
            ))}
          </Route>
        </Route>

        <Route path={ROUTES.ROOT} element={<Navigate to={ROUTES.LOGIN} replace />} />
        <Route path="*" element={<Navigate to={ROUTES.LOGIN} replace />} />
      </Routes>
    </Suspense>
  );
}

export default AppRoutes;
